// Premium AI Service for Structured Trip Recommendations
// Generates 3 options for each category and schedules them over the trip period

import { jsonrepair } from 'jsonrepair';
import { AI_SUGGESTION_RADIUS_MILES } from '../utils/partnerTripAiContext';
import { chatCompletion } from './backendAIClient';

const RADIUS_RULE = (miles = AI_SUGGESTION_RADIUS_MILES) => (
  `HARD RADIUS: Every venue MUST be within ${miles} miles of a trip city listed in the preferences. `
  + `Reject anything farther away (other metros, day-trip cities, airports outside the radius). `
  + `Include numeric "lat" and "lng" for each item when known. `
  + `If you are unsure it is inside the ${miles}-mile radius, omit it.`
);

export function destinationFromAiContext(context = '') {
  const lines = String(context).split(/\r?\n/);
  const toLine = lines.find((line) => /^To:\s+/i.test(line));
  if (toLine) {
    const value = toLine.replace(/^To:\s+/i, '').trim();
    if (value) return value;
  }
  const destLine = lines.find((line) => /^Destinations:\s+/i.test(line));
  if (destLine) {
    const value = destLine.replace(/^Destinations:\s+/i, '').split(',')[0].trim();
    if (value) return value;
  }
  return 'Destination';
}

/** Coerce AI duration fields (often a bare number like 10) into a day count. */
export function normalizeTripDurationDays(duration, fallback = 7) {
  if (typeof duration === 'number' && Number.isFinite(duration) && duration > 0) {
    return Math.max(1, Math.round(duration));
  }
  const text = String(duration ?? '').trim();
  if (!text) return fallback;
  const match = text.match(/(\d+(?:\.\d+)?)/);
  if (!match) return fallback;
  const days = Math.round(Number(match[1]));
  return Number.isFinite(days) && days > 0 ? days : fallback;
}

export function normalizeTripDurationLabel(duration, fallbackDays = 7) {
  const days = normalizeTripDurationDays(duration, fallbackDays);
  return `${days} days`;
}

export function normalizeTripDestinations(destinations, fallback = ['Unknown']) {
  if (Array.isArray(destinations)) {
    const cleaned = destinations.map((entry) => String(entry || '').trim()).filter(Boolean);
    return cleaned.length ? cleaned : fallback;
  }
  if (typeof destinations === 'string' && destinations.trim()) {
    return destinations.split(',').map((entry) => entry.trim()).filter(Boolean);
  }
  return fallback;
}

const NAMED_KEYS = ['name', 'title', 'placeName', 'hotelName', 'restaurantName', 'locationName'];
const LIST_KEYS = ['items', 'locations', 'hotels', 'restaurants', 'activities', 'recommendations', 'results', 'data'];

export function cleanAiJsonText(content) {
  let text = String(content || '')
    .replace(/^\uFEFF/, '')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2018\u2019]/g, "'");
  text = text.replace(/```(?:json|javascript|js|JSON)?\s*/gi, '').replace(/```/g, '');
  text = text.trim();
  const firstObj = text.indexOf('{');
  const firstArr = text.indexOf('[');
  const startCandidates = [firstObj, firstArr].filter((index) => index >= 0);
  if (startCandidates.length) {
    text = text.slice(Math.min(...startCandidates));
  }
  const lastBrace = Math.max(text.lastIndexOf('}'), text.lastIndexOf(']'));
  if (lastBrace >= 0 && lastBrace < text.length - 1) {
    const trailing = text.slice(lastBrace + 1).trim();
    const moreJson = /^[,[{"]/.test(trailing) || /"(?:name|title|items|city)"/.test(trailing);
    if (trailing && !moreJson) {
      text = text.slice(0, lastBrace + 1);
    }
  }
  return text.trim();
}

function closeUnbalancedJson(text) {
  let inString = false;
  let escape = false;
  let braces = 0;
  let brackets = 0;
  for (const ch of text) {
    if (inString) {
      if (escape) escape = false;
      else if (ch === '\\') escape = true;
      else if (ch === '"') inString = false;
      continue;
    }
    if (ch === '"') inString = true;
    else if (ch === '{') braces += 1;
    else if (ch === '}') braces -= 1;
    else if (ch === '[') brackets += 1;
    else if (ch === ']') brackets -= 1;
  }
  let closed = text;
  if (inString) closed += '"';
  if (braces < 0 || brackets < 0) return closed;
  return `${closed}${']'.repeat(brackets)}${'}'.repeat(braces)}`;
}

function tryParseJson(text) {
  if (!text) return null;
  const candidates = [text];
  try { candidates.push(jsonrepair(text)); } catch { /* ignore */ }
  candidates.push(closeUnbalancedJson(text));
  try { candidates.push(jsonrepair(closeUnbalancedJson(text))); } catch { /* ignore */ }
  for (const candidate of candidates) {
    try {
      return JSON.parse(candidate);
    } catch {
      // try the next candidate
    }
  }
  return null;
}

export function parseJsonValue(text) {
  const cleaned = cleanAiJsonText(text);
  return tryParseJson(cleaned) || tryParseJson(text);
}

function namedItem(item) {
  if (!item || typeof item !== 'object' || Array.isArray(item)) return null;
  const name = NAMED_KEYS.map((key) => item[key]).find((value) => String(value || '').trim());
  if (!name) return null;
  return { ...item, name: String(name).trim() };
}

function recoverNamedObjects(text) {
  const cleaned = cleanAiJsonText(text);
  const items = [];
  const seen = new Set();
  const pattern = /"(?:name|title)"\s*:\s*"((?:\\.|[^"\\])*)"/g;
  let match = pattern.exec(cleaned);
  while (match) {
    const name = match[1].replace(/\\"/g, '"').trim();
    if (name && !seen.has(name.toLowerCase())) {
      seen.add(name.toLowerCase());
      items.push({ name });
    }
    match = pattern.exec(cleaned);
  }
  return items;
}

function listFromParsedValue(parsed, category, depth = 0) {
  if (parsed == null || depth > 5) return [];
  if (typeof parsed === 'string') {
    const nested = parseJsonValue(parsed);
    return nested ? listFromParsedValue(nested, category, depth + 1) : [];
  }
  if (Array.isArray(parsed)) return parsed.map(namedItem).filter(Boolean);
  if (typeof parsed !== 'object') return [];

  if (category && Array.isArray(parsed[category])) {
    const fromCategory = parsed[category].map(namedItem).filter(Boolean);
    if (fromCategory.length) return fromCategory;
  }

  for (const key of LIST_KEYS) {
    if (!Array.isArray(parsed[key])) continue;
    const items = parsed[key].map(namedItem).filter(Boolean);
    if (items.length) return items;
  }

  for (const value of Object.values(parsed)) {
    if (!value || (typeof value !== 'object' && typeof value !== 'string')) continue;
    const nested = listFromParsedValue(value, category, depth + 1);
    if (nested.length) return nested;
  }
  return [];
}

export function parseAiJsonObject(content) {
  if (content && typeof content === 'object' && !Array.isArray(content)) return content;
  if (Array.isArray(content) && content[0] && typeof content[0] === 'object') return content[0];
  if (!content || typeof content !== 'string') return null;
  const parsed = parseJsonValue(content);
  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
  if (Array.isArray(parsed) && parsed[0] && typeof parsed[0] === 'object') return parsed[0];
  return null;
}

export function parseAiJsonList(content, category) {
  let parsed = [];
  if (Array.isArray(content) || (content && typeof content === 'object')) {
    parsed = listFromParsedValue(content, category);
  } else if (content && typeof content === 'string') {
    parsed = listFromParsedValue(parseJsonValue(content), category);
  }
  const recovered = typeof content === 'string' ? recoverNamedObjects(content) : [];
  if (!parsed.length) return recovered;
  const seen = new Set(parsed.map((item) => item.name.toLowerCase()));
  recovered.forEach((item) => {
    if (!seen.has(item.name.toLowerCase())) parsed.push(item);
  });
  return parsed;
}

class PremiumAIService {
  constructor({ usePartnerAuth = false, tripId = null } = {}) {
    this.model = 'gpt-4o'; // Better token limits and quality for detailed responses
    this.version = '4.0.0'; // Parallel AI calls system
    this.timestamp = Date.now(); // Force cache invalidation
    this.usePartnerAuth = usePartnerAuth;
    this.tripId = tripId;
    this.lastUsage = { tokens: 0, modelCost: 0, model: this.model };
  }

  resetUsageTotals() {
    this.lastUsage = { tokens: 0, modelCost: 0, model: this.model };
  }

  estimateModelCost(usage, model = this.model) {
    if (!usage) return 0;
    const rates = {
      'gpt-4o': { input: 2.5 / 1_000_000, output: 10 / 1_000_000 },
      'gpt-4o-mini': { input: 0.15 / 1_000_000, output: 0.6 / 1_000_000 }
    };
    const rate = rates[model] || rates['gpt-4o'];
    const promptTokens = Number(usage.prompt_tokens) || 0;
    const completionTokens = Number(usage.completion_tokens) || 0;
    return (promptTokens * rate.input) + (completionTokens * rate.output);
  }

  trackUsage(result) {
    if (!result?.usage) return;
    this.lastUsage.tokens += Number(result.usage.total_tokens) || 0;
    this.lastUsage.modelCost += this.estimateModelCost(result.usage, result.model || this.model);
    this.lastUsage.model = result.model || this.model;
  }

  getUsageTotals() {
    return { ...this.lastUsage };
  }

  // Generate trip basic info (name, duration, destinations)
  async generateTripInfo(context) {
    const prompt = `Generate basic trip information based on user preferences.

    USER PREFERENCES:
    ${context}

    IMPORTANT: Use the EXACT locations, dates, and preferences provided above. Do not use generic examples or placeholder data.

    Return ONLY a JSON object with this structure. Do not use markdown.
    {
      "tripName": "Generated trip name based on preferences",
      "duration": "X days",
      "destinations": ["city1", "city2", "city3"]
    }`;

    try {
      const response = await this.makeAICall(prompt);
      const content = typeof response === 'string' ? response : response.content;
      const parsed = parseAiJsonObject(content);
      if (parsed?.tripName || parsed?.destinations || parsed?.duration) {
        return {
          tripName: parsed.tripName || 'AI-Generated Trip',
          duration: normalizeTripDurationLabel(parsed.duration),
          destinations: normalizeTripDestinations(parsed.destinations)
        };
      }
      return {
        tripName: 'AI-Generated Trip',
        duration: '7 days',
        destinations: ['Unknown']
      };
    } catch (error) {
      console.error('❌ Error generating trip info:', error);
      if (/openai api error|incorrect api key|invalid_api_key|insufficient_quota|429/i.test(String(error?.message || ''))) {
        throw error;
      }
      return {
        tripName: 'AI-Generated Trip',
        duration: '7 days',
        destinations: ['Unknown']
      };
    }
  }

  // Generate recommendations for a specific category
  async generateCategoryRecommendations(category, context) {
    const categoryPrompts = {
      locations: `Generate 6 location recommendations for a trip.

      USER PREFERENCES:
      ${context}

      IMPORTANT: Use the EXACT locations, dates, and preferences provided above. Focus on the specific destination mentioned in the preferences.

      CRITICAL REQUIREMENTS:
      - Return ONLY a JSON object: {"items":[...]}
      - Do not wrap the JSON in markdown fences
      - ${RADIUS_RULE()}
      - Prefer venues from any nearby/popular shortlist in the preferences
      - Include ALL required fields for each location
      - Provide realistic, detailed information
      - Use actual addresses, phone numbers, and websites when possible
      - Include multiple reviews for each location

      Return a JSON object {"items":[...]} of location objects:
      { "items": [
        {
          "name": "Location Name",
          "city": "City Name",
          "lat": 25.2048,
          "lng": 55.2708,
          "description": "Detailed description of the location and what makes it special",
          "whyRecommended": "Why this location is recommended for this specific trip",
          "estimatedCost": "$X per person",
          "bestTime": "Morning/Afternoon/Evening",
          "category": "landmark/museum/park/viewpoint",
          "address": "Full street address",
          "phone": "Phone number with area code",
          "website": "Official website URL",
          "googleMapsUrl": "https://www.google.com/maps/search/?api=1&query=FULL_ADDRESS_HERE",
          "rating": "X.X/5",
          "hours": "Operating hours (e.g., '9:00 AM - 5:00 PM')",
          "amenities": ["wifi", "parking", "restrooms", "accessibility"],
          "nearbyAttractions": [{"name": "Nearby attraction name", "distance": "0.5 miles"}],
          "transportation": "How to get there (Metro, bus, taxi, walking)",
          "reviews": [{"text": "Customer review text", "author": "Reviewer name"}]
        }
      ]}`,

      hotels: `Generate 6 hotel recommendations for a trip.

      USER PREFERENCES:
      ${context}

      IMPORTANT: Use the EXACT locations, dates, and preferences provided above. Focus on hotels in the specific destination mentioned in the preferences.

      CRITICAL REQUIREMENTS:
      - Return ONLY a JSON object: {"items":[...]}
      - Do not wrap the JSON in markdown fences
      - ${RADIUS_RULE()}
      - Prefer venues from any nearby/popular shortlist in the preferences
      - Include ALL required fields for each hotel
      - Provide realistic, detailed information
      - Use actual addresses, phone numbers, and websites when possible
      - starRating is the official hotel class (1-5), not the guest review score
      - rating is the guest review score out of 5
      - Include multiple reviews for each hotel

      Return a JSON object {"items":[...]} of hotel objects:
      { "items": [
        {
          "name": "Hotel Name",
          "city": "City Name",
          "lat": 25.2048,
          "lng": 55.2708,
          "description": "Detailed description of the hotel and its unique features",
          "whyRecommended": "Why this hotel is recommended for this specific trip",
          "priceRange": "$X-$Y per night",
          "starRating": 5,
          "rating": "X.X/5",
          "location": "Location description (downtown, near airport, etc.)",
          "address": "Full street address",
          "phone": "Phone number with area code",
          "website": "Official website URL",
          "googleMapsUrl": "https://www.google.com/maps/search/?api=1&query=FULL_ADDRESS_HERE",
          "bookingUrl": "Booking URL (Booking.com, Expedia, etc.)",
          "amenities": ["wifi", "pool", "spa", "restaurant", "parking"],
          "capacity": "Guest capacity (1-2 guests, 2-4 guests, etc.)",
          "hours": "Check-in/out times (e.g., '3:00 PM - 11:00 AM')",
          "reviews": [{"text": "Customer review text", "author": "Reviewer name"}]
        }
      ]}`,

      restaurants: `Generate 6 restaurant recommendations for a trip.

      USER PREFERENCES:
      ${context}

      IMPORTANT: Use the EXACT locations, dates, and preferences provided above. Focus on restaurants in the specific destination mentioned in the preferences.

      CRITICAL REQUIREMENTS:
      - Return ONLY a JSON object: {"items":[...]}
      - Do not wrap the JSON in markdown fences
      - ${RADIUS_RULE()}
      - Prefer venues from any nearby/popular shortlist in the preferences
      - Include ALL required fields for each restaurant
      - Provide realistic, detailed information
      - Use actual addresses, phone numbers, and websites when possible
      - Include multiple reviews for each restaurant

      Return a JSON object {"items":[...]} of restaurant objects:
      { "items": [
        {
          "name": "Restaurant Name",
          "city": "City Name",
          "lat": 25.2048,
          "lng": 55.2708,
          "cuisine": "Cuisine type (Italian, Mexican, Asian, etc.)",
          "description": "Detailed description of the restaurant and its specialties",
          "whyRecommended": "Why this restaurant is recommended for this specific trip",
          "priceRange": "$X-$Y per person",
          "rating": "X.X/5",
          "bestFor": "Breakfast/Lunch/Dinner",
          "address": "Full street address",
          "phone": "Phone number with area code",
          "website": "Official website URL",
          "googleMapsUrl": "https://www.google.com/maps/search/?api=1&query=FULL_ADDRESS_HERE",
          "bookingUrl": "Reservation URL (OpenTable, Resy, etc.)",
          "hours": "Operating hours (e.g., '11:00 AM - 10:00 PM')",
          "amenities": ["wifi", "outdoor_seating", "bar", "parking"],
          "reviews": [{"text": "Customer review text", "author": "Reviewer name"}]
        }
      ]}`,

      activities: `Generate 6 activity recommendations for a trip.

      USER PREFERENCES:
      ${context}

      IMPORTANT: Use the EXACT locations, dates, and preferences provided above. Focus on activities in the specific destination mentioned in the preferences.

      CRITICAL REQUIREMENTS:
      - Return ONLY a JSON object: {"items":[...]}
      - Do not wrap the JSON in markdown fences
      - ${RADIUS_RULE()}
      - Prefer venues from any nearby/popular shortlist in the preferences
      - Include ALL required fields for each activity
      - Provide realistic, detailed information
      - Use actual addresses, phone numbers, and websites when possible
      - Include multiple reviews for each activity

      Return a JSON object {"items":[...]} of activity objects:
      { "items": [
        {
          "name": "Activity Name",
          "city": "City Name",
          "lat": 25.2048,
          "lng": 55.2708,
          "type": "Activity type (tour, adventure, cultural, etc.)",
          "description": "Detailed description of the activity and what makes it special",
          "whyRecommended": "Why this activity is recommended for this specific trip",
          "duration": "Duration (e.g., '2 hours', 'Half day', 'Full day')",
          "priceRange": "$X-$Y per person",
          "difficulty": "Easy/Moderate/Hard",
          "address": "Full street address",
          "phone": "Phone number with area code",
          "website": "Official website URL",
          "googleMapsUrl": "https://www.google.com/maps/search/?api=1&query=FULL_ADDRESS_HERE",
          "bookingUrl": "https://www.viator.com search or product URL if you know one; omit if unknown",
          "hours": "Operating hours (e.g., '9:00 AM - 5:00 PM')",
          "capacity": "Capacity (e.g., 'Up to 20 people', 'Small groups')",
          "amenities": ["guide", "equipment", "transportation", "refreshments"],
          "reviews": [{"text": "Customer review text", "author": "Reviewer name"}]
        }
      ]}`
    };

    try {
      const response = await this.makeAICall(categoryPrompts[category]);
      const content = typeof response === 'string' ? response : response.content;
      const items = parseAiJsonList(content, category);
      if (!items.length) {
        console.warn(`⚠️ ${category} returned no named recommendations`);
      }
      return items;
    } catch (error) {
      console.error(`❌ Error generating ${category} recommendations:`, error);
      // Auth / quota failures must bubble — swallowing them made every destination
      // look like a generic "Unable to generate recommendations" empty result.
      if (/openai api error|incorrect api key|invalid_api_key|insufficient_quota|429/i.test(String(error?.message || ''))) {
        throw error;
      }
      return [];
    }
  }

  // Make AI call via Railway proxy (key stays server-side)
  async makeAICall(prompt) {
    try {
      const data = await chatCompletion({
        purpose: 'trip_recommendations',
        model: this.model,
        messages: [
          {
            role: 'system',
            content: 'You are a JSON generator. Return ONLY a valid JSON object. Never use markdown fences, comments, or explanations. If the result is a list, wrap it as {"items":[...]}. Close every object and array. No trailing commas.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        responseFormat: 'json_object',
        maxTokens: 8000,
        temperature: 0.7,
        tripId: this.tripId,
        usePartnerAuth: this.usePartnerAuth,
      });
      const result = {
        content: data.content,
        usage: data.usage || null,
        model: data.model || this.model
      };
      this.trackUsage(result);
      return result;
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Unknown error';
      throw new Error(`OpenAI API error: ${message}`);
    }
  }

  // Generate structured trip recommendations using parallel AI calls
  async generateStructuredRecommendations(preferences, { onProgress } = {}) {
    const report = (update) => {
      try {
        onProgress?.(update);
      } catch (progressError) {
        console.warn('AI progress callback failed:', progressError.message);
      }
    };

    try {
      this.resetUsageTotals();
      console.log('🔍 Generating recommendations with preferences:', preferences);
      const context = this.buildContextFromPreferences(preferences);
      console.log('📝 Built context:', context);
      console.log('📏 Context length:', context.length);
      
      console.log('🚀 Starting parallel AI calls for all categories...');
      
      report({ step: 'tripInfo', status: 'active' });
      const tripInfo = await this.generateTripInfo(context);
      report({ step: 'tripInfo', status: 'done' });
      console.log('✅ Trip info generated:', tripInfo);
      
      const categories = ['hotels', 'restaurants', 'locations', 'activities'];
      categories.forEach((category) => {
        report({ step: category, status: 'active' });
      });

      console.log('🔄 Generating categories in parallel...');
      const settled = await Promise.all(
        categories.map(async (category) => {
          const items = await this.generateCategoryRecommendations(category, context);
          report({ step: category, status: 'done', count: items.length });
          return items;
        })
      );
      const [hotels, restaurants, locations, activities] = settled;
      
      console.log('✅ All categories generated:', {
        locations: locations.length,
        hotels: hotels.length,
        restaurants: restaurants.length,
        activities: activities.length
      });
      
      // Combine all results
      const recommendations = {
        tripName: tripInfo.tripName,
        duration: tripInfo.duration,
        destinations: tripInfo.destinations,
        locations,
        hotels,
        restaurants,
        activities
      };
      
      console.log('🔍 Generated Categories:', Object.keys(recommendations));
      console.log('🔍 Category Counts:', {
        locations: recommendations.locations?.length || 0,
        hotels: recommendations.hotels?.length || 0,
        restaurants: recommendations.restaurants?.length || 0,
        activities: recommendations.activities?.length || 0
      });
      
      // Validate that we have sufficient options in each category
      const categoryCounts = {
        locations: recommendations.locations?.length || 0,
        hotels: recommendations.hotels?.length || 0,
        restaurants: recommendations.restaurants?.length || 0,
        activities: recommendations.activities?.length || 0
      };
      
      console.log('🔍 Validating AI response:');
      Object.entries(categoryCounts).forEach(([category, count]) => {
        console.log(`  ${category}: ${count} options`);
        if (count < 1) {
          console.warn(`⚠️ ${category} has ${count} options (expected at least 1)`);
        }
      });
      
      if (Object.values(categoryCounts).every((count) => count < 1)) {
        throw new Error('Unable to generate recommendations');
      }

      report({ step: 'finalize', status: 'done' });
      const fixedRecommendations = this.fixGoogleMapsUrls(recommendations);
      const scheduled = this.convertToScheduledItinerary(fixedRecommendations);
      return {
        ...scheduled,
        aiUsage: { ...this.lastUsage }
      };
    } catch (error) {
      console.error('❌ Error generating structured recommendations:', error);
      throw error;
    }
  }

  // Fix Google Maps URLs by replacing placeholder with actual addresses
  fixGoogleMapsUrls(recommendations) {
    const categories = ['locations', 'hotels', 'restaurants', 'activities'];
    
    categories.forEach(category => {
      if (recommendations[category]) {
        recommendations[category] = recommendations[category].map(item => {
          const query = item.address || [item.name, item.city].filter(Boolean).join(', ');
          const mapsUrl = String(item.googleMapsUrl || '');
          if (query && (!mapsUrl || mapsUrl.includes('FULL_ADDRESS'))) {
            item.googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
          }
          return item;
        });
      }
    });
    
    return recommendations;
  }

  // Helper function to extract city name from full address
  extractCityName(fullAddress) {
    if (!fullAddress) return 'Unknown';
    
    // Split by comma and take the first part (city name)
    const parts = fullAddress.split(',');
    return parts[0].trim();
  }

  // Convert flat recommendations to scheduled itinerary format
  convertToScheduledItinerary(recommendations) {
    const { tripName, locations, hotels, restaurants, activities } = recommendations;
    const destinations = normalizeTripDestinations(recommendations.destinations);
    const numDays = normalizeTripDurationDays(recommendations.duration, 3);
    const duration = normalizeTripDurationLabel(recommendations.duration, numDays);

    // Create scheduled itinerary with distributed recommendations
    const scheduledItinerary = [];
    for (let day = 1; day <= numDays; day++) {
      const dayDate = new Date();
      dayDate.setDate(dayDate.getDate() + day - 1);

      // Show ALL recommendations for each day (don't distribute)
      scheduledItinerary.push({
        day: day,
        date: dayDate.toISOString().split('T')[0],
        city: this.extractCityName(destinations[0]),
        locations: locations || [],
        hotels: hotels || [],
        restaurants: restaurants || [],
        activities: activities || []
      });
    }

    return {
      tripName,
      duration,
      destinations,
      locations,
      hotels,
      restaurants,
      activities,
      scheduledItinerary,
      totalDays: numDays
    };
  }

  // Build context from preferences
  buildContextFromPreferences(preferences) {
    const context = [];
    
    // Debug logging
    console.log('🤖 Premium AI Service - buildContextFromPreferences called with:', preferences);
    console.log('🤖 Premium AI Service - fromLocation:', preferences.fromLocation);
    console.log('🤖 Premium AI Service - toLocation:', preferences.toLocation);
    
    // Trip basics
    if (preferences.tripName) context.push(`Trip Name: ${preferences.tripName}`);
    if (preferences.fromLocation) context.push(`From: ${preferences.fromLocation}`);
    const destinationList = Array.isArray(preferences.destinations)
      ? preferences.destinations.filter(Boolean).join(', ')
      : preferences.destinations;
    if (destinationList) context.push(`Destinations: ${destinationList}`);
    if (preferences.toLocation) context.push(`To: ${preferences.toLocation}`);
    else if (destinationList) context.push(`To: ${destinationList}`);
    if (preferences.startDate) context.push(`Start Date: ${preferences.startDate}`);
    if (preferences.endDate) context.push(`End Date: ${preferences.endDate}`);
    if (preferences.travelers) context.push(`Number of Travelers: ${preferences.travelers}`);
    if (preferences.flexibleDates) context.push(`Flexible Dates: Yes`);
    if (preferences.clientTier) {
      context.push(`Client spend tier: ${preferences.clientTier}. Match every hotel, restaurant, and activity to this tier only.`);
    }
    
    // Budget & Preferences
    if (preferences.budget) {
      const budgetType = preferences.budgetType === 'per_person' ? 'per person' : 'total';
      context.push(`Budget: $${preferences.budget} (${budgetType})`);
    }
    if (preferences.tripTypes && preferences.tripTypes.length > 0) {
      context.push(`Trip Types: ${preferences.tripTypes.join(', ')}`);
    }
    if (preferences.preferredPace) {
      context.push(`Preferred Pace: ${preferences.preferredPace}`);
    }
    
    // Accommodation & Transport
    if (preferences.accommodationStyles && preferences.accommodationStyles.length > 0) {
      context.push(`Accommodation Styles: ${preferences.accommodationStyles.join(', ')}`);
    }
    if (preferences.transportPreferences && preferences.transportPreferences.length > 0) {
      context.push(`Transport Preferences: ${preferences.transportPreferences.join(', ')}`);
    }
    
    // Restaurant Preferences
    if (preferences.cuisineTypes && preferences.cuisineTypes.length > 0) {
      context.push(`Cuisine Types: ${preferences.cuisineTypes.join(', ')}`);
    }
    if (preferences.dietaryRestrictions) {
      context.push(`Dietary Restrictions: ${preferences.dietaryRestrictions}`);
    }
    if (preferences.diningStyle && preferences.diningStyle.length > 0) {
      context.push(`Dining Style: ${preferences.diningStyle.join(', ')}`);
    }
    if (preferences.priceRange) {
      context.push(`Price Range: ${preferences.priceRange}`);
    }
    if (preferences.specialRequests) {
      context.push(`Special Requests: ${preferences.specialRequests}`);
    }
    
    // Activities & Interests
    if (preferences.interests && preferences.interests.length > 0) {
      context.push(`Interests: ${preferences.interests.join(', ')}`);
    }
    if (preferences.mustSeeItems) {
      context.push(`Must-See Items: ${preferences.mustSeeItems}`);
    }
    if (preferences.existingItinerary) {
      context.push(`ALREADY ON THE ITINERARY (do not repeat or near-duplicate these):\n${preferences.existingItinerary}`);
    }
    if (destinationList) {
      context.push(
        `HARD RADIUS: Stay within ${AI_SUGGESTION_RADIUS_MILES} miles of any of these trip cities: ${destinationList}. `
        + `Every suggestion must be inside that radius of at least one listed city, not a street or airport. `
        + `Omit anything farther than ${AI_SUGGESTION_RADIUS_MILES} miles.`
      );
    }
    const coords = Array.isArray(preferences.destinationCoords) ? preferences.destinationCoords : [];
    if (coords.length) {
      const line = coords
        .filter((point) => Number.isFinite(Number(point?.lat)) && Number.isFinite(Number(point?.lng)))
        .map((point) => `${point.name || 'city'} (${Number(point.lat).toFixed(4)}, ${Number(point.lng).toFixed(4)})`)
        .join('; ');
      if (line) {
        context.push(`Trip city centers (lat,lng) — measure the ${AI_SUGGESTION_RADIUS_MILES}-mile radius from these: ${line}`);
      }
    }
    if (preferences.nearbyCatalog) {
      context.push(`NEARBY / POPULAR SHORTLIST — prefer these when they fit:\n${preferences.nearbyCatalog}`);
    }
    if (preferences.additionalContext) {
      context.push(`Additional context:\n${preferences.additionalContext}`);
    }
    
    return context.join('\n');
  }

  // Create fallback recommendations when AI fails
  createFallbackRecommendations(context = '') {
    console.log('🔄 Creating fallback recommendations with 10-15 options per category');
    console.log('⚠️ FALLBACK MODE: Using hardcoded recommendations instead of AI');
    console.log('📝 Context for fallback:', context);
    
    // Extract destination from context if available
    const destination = destinationFromAiContext(context);
    
    const fallbackData = {
      tripName: `${destination} Adventure`,
      duration: '3 days',
      destinations: [destination],
      locations: [
        {
          name: `${destination} City Center`,
          city: destination,
          description: `Explore the heart of ${destination}`,
          whyRecommended: 'Perfect starting point for your visit',
          estimatedCost: 'Free',
          bestTime: 'Morning',
          category: 'landmark',
          address: `City Center, ${destination}`,
          phone: 'N/A',
          website: 'N/A',
          googleMapsUrl: `https://maps.google.com/?q=${encodeURIComponent(destination)}`,
          rating: '4.0/5',
          hours: '24/7',
          amenities: ['accessibility'],
          nearbyAttractions: [],
          transportation: 'Walking, taxi, or public transport',
          reviews: [
            { text: `Great place to start exploring ${destination}`, author: 'Traveler' }
          ]
        }
      ],
      hotels: [
        {
          name: 'Hotel Ritz Paris',
          city: 'Paris',
          description: 'Luxurious 5-star hotel in the heart of Paris',
          whyRecommended: 'Perfect for luxury travelers seeking elegance',
          priceRange: '$800-$1200 per night',
          rating: '4.8/5',
          location: 'Place Vendôme, 1st arrondissement',
          address: '15 Place Vendôme, 75001 Paris',
          phone: '+33 1 43 16 30 30',
          website: 'https://www.ritzparis.com',
          googleMapsUrl: 'https://goo.gl/maps/ritz',
          bookingUrl: 'https://www.ritzparis.com',
          amenities: ['wifi', 'pool', 'spa', 'restaurant'],
          capacity: '2-4 guests per room',
          hours: 'Check-in: 3:00 PM, Check-out: 12:00 PM',
          reviews: [
            { text: 'Exceptional service and elegance', author: 'Sarah M.' }
          ]
        }
      ],
      restaurants: [
        {
          name: 'Le Comptoir du Relais',
          city: 'Paris',
          cuisine: 'French',
          description: 'Cozy bistro serving traditional French cuisine',
          whyRecommended: 'Authentic Parisian dining experience',
          priceRange: '$40-$60 per person',
          rating: '4.5/5',
          bestFor: 'Dinner',
          address: '9 Carrefour de l\'Odéon, 75006 Paris',
          phone: '+33 1 44 27 07 97',
          website: 'https://www.hotel-paris-relais-saint-germain.com',
          googleMapsUrl: 'https://goo.gl/maps/comptoir',
          bookingUrl: 'https://www.opentable.com',
          hours: '12:00 PM - 2:00 PM, 7:00 PM - 10:00 PM',
          amenities: ['wifi', 'outdoor_seating'],
          reviews: [
            { text: 'Delicious traditional French food', author: 'Mike L.' }
          ]
        }
      ],
      activities: [
        {
          name: 'Seine River Cruise',
          city: 'Paris',
          type: 'Sightseeing',
          description: 'Scenic boat cruise along the Seine River',
          whyRecommended: 'Perfect way to see Paris from the water',
          duration: '1 hour',
          priceRange: '$15-$25 per person',
          difficulty: 'Easy',
          address: 'Port de la Bourdonnais, 75007 Paris',
          phone: '+33 1 42 25 96 10',
          website: 'https://www.bateaux-mouches.fr',
          googleMapsUrl: 'https://goo.gl/maps/cruise',
          bookingUrl: 'https://www.bateaux-mouches.fr',
          hours: '10:00 AM - 10:00 PM',
          capacity: 'Unlimited',
          amenities: ['guide', 'audio_guide'],
          reviews: [
            { text: 'Beautiful views of Paris landmarks', author: 'Lisa K.' }
          ]
        }
      ]
    };
    
    // Convert fallback data to scheduled itinerary format
    return this.convertToScheduledItinerary(fallbackData);
  }
}

export default PremiumAIService;